import { useState, useEffect, useRef } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import styles from './result-table-row.module.scss';
import { augmentClassName } from '../../../../utils/GlobalCssProxy';
import { urls } from '../../../../utils/url-helper/index';
import Tag from '../../../molecules/screener/tag';
import HypotheticalGrowthChart from '../hypothetical-growth-chart';
import CreditRatingChart from '../credit-rating-chart';
import FundDetailTable from '../fund-detail-table';
import ButtonIconLink from '../../../atoms/button-icon-link';
import FundDetailButtons from '../fund-detail-buttons';
import NetGrossDisclosureButton from '../net-gross-disclosure-button';
import InvestmentName from '../investment-name';
import RatingRow from '../morningstar-rating-row';
import Button from 'react-bootstrap/Button';
import ModalInvest from '../../profile/page-header/modals/modal-invest';
import DocumentsModal from '../documents-modal';
import Icon from '../../../atoms/icon';
import PerformanceElement from '../performance-element';
import InteractiveElement from '../../../atoms/interactive-element';
// import AssetAllocationsChart from '../asset-allocations-chart';
import MVPEmptyState from '../../../../assets/images/MVPEmptyState.svg';
import { FIELDS, FOOTNOTE_DISCLAIMERS_NUMBERS } from '../../../../shared/screener-constants';
import {
  getTextValueColorClass,
  alignData,
  getIsStandardPerformance,
  shouldShowHypoChart,
  isTiaaFixedAnnuity,
  isShowNetGrossExpenseRatioDisclosure
} from '../../../../utils/sharedHelpers';
import useRowStore, { INVEST_BUTTON_MODES } from '../../../../stores/screener/use-row-store';
import globals from '../../../../utils/globals';
import format from '../../../../utils/format';
import PerformanceValue from '../../../../components/atoms/performance-value';
import { isCIT } from '../../../../utils/fund-type-helpers/fundTypeHelpers';

const className = augmentClassName(styles);

export const ResultTableRow = ({
  item,
  index,
  isExpanded,
  isSignatureResult,
  onClickExpandRow,
  performanceQuarterly,
  participantHoldings,
  performanceAsOf,
  isSearchedPlanInContext,
  searchedHoldings,
  showSalesCharge,
  planInfo
}) => {
  const [expandTags, setExpandTags] = useState(false);
  const [outBoundsTagIndex, setOutBoundsTagIndex] = useState(-1);
  const [lastTagIndex, setLastTagIndex] = useState(-1);
  const tagRef = useRef([]);
  const fundType = item[FIELDS.TCFUNDTYPE];
  const fundTicker = item[FIELDS.TCTICKER];
  const productType = item[FIELDS.PRODUCTTYPE];
  const assetClass = item[FIELDS.ASSETCLASS];
  const shareClass = item[FIELDS.TCSHARECLASS];
  const isProp = item[FIELDS.TCTREATASPROP] > 0;
  const underlyingFundIncepDate = item[FIELDS.TCUNDERLYINGFUNDINCEPTIONDATE];
  const xid = item[FIELDS.TCWSODISSUE];
  const [showModal, setShowModal] = useState(false);
  const [modalTitle, setModalTitle] = useState('');
  const [modalContent, setModalContent] = useState('');
  const isStandardPerformance = getIsStandardPerformance(item, performanceQuarterly);
  const hasNetExpense = item[FIELDS.TCNETEXPENSES];
  const hasGrossExpense = item[FIELDS.TCGROSSEXPENSE];
  const showNetGrossDisclosure = isShowNetGrossExpenseRatioDisclosure(
    item[FIELDS.TCNETEXPENSES],
    item[FIELDS.TCGROSSEXPENSE]
  );
  const isCITFund = isCIT(isProp, fundType);
  const showSalesChargeData = showSalesCharge && item[FIELDS.TCSHARECLASS] === 'Class A';

  const {
    channel,
    fundDetailTableData,
    investButtonMode,
    isInvestModalVisible,
    isPlanInContext,
    handleClickInvestButton,
    handleCloseInvestModal,
    restrictionText,
    fundName,
    UrlDomain,
    UrlPrefix
  } = useRowStore({
    fundType,
    xid,
    item,
    isSearchedPlanInContext,
    participantHoldings,
    searchedHoldings,
    planInfo
  });
  const {
    yearToDateReturn,
    oneYearReturn,
    threeYearReturn,
    fiveYearReturn,
    tenYearReturn,
    sinceIncep,
    inceptionDate,
    asOfDate
  } = alignData(item, performanceQuarterly, performanceAsOf);

  const isShowUnderlyingFundIncepDate =
    !isStandardPerformance &&
    underlyingFundIncepDate !== inceptionDate &&
    underlyingFundIncepDate !== null;

  const joinedRestrictions = restrictionText?.join('<br></br>');

  const collapsedColor = isSignatureResult
    ? 'signature-collapsed-color'
    : 'default-collapsed-color';

  const showFundAsOfDate = asOfDate !== performanceAsOf;
  const onClickInvestButton = e => {
    e.stopPropagation();
    if (investButtonMode === INVEST_BUTTON_MODES.modal) {
      handleClickInvestButton();
    } else if (investButtonMode === INVEST_BUTTON_MODES.link) {
      window.open(globals.investButtonUrl, '_blank');
    }
  };

  const profileUrl = urls.pages.linkToProfile({
    xid: item[FIELDS.TCWSODISSUE],
    channel,
    UrlDomain,
    UrlPrefix
  });

  const tags = expandTags ? item.tags : item.collapseTags;

  const setModal = (title, content) => {
    setModalTitle(title);
    setModalContent(content);
    setShowModal(true);
  };

  useEffect(() => {
    if (expandTags) {
      let totalWidth = 16;
      //The rows on the table can be pulled from the result-table element minus the left and right padding. The rows are composed of individual elements so the width can not be pulled by referencing the row. The expand row button is 16px wide. Subtract that also. If necessary these elements can be grabbed to pull their values dynamically.
      const rowContainerWidth = document.getElementById('result-table').clientWidth - 40 - 16;
      const tagElements = tagRef.current;
      for (let i = 0; i < tagElements.length; i++) {
        totalWidth += tagElements[i].clientWidth;
        if (totalWidth > rowContainerWidth) {
          setOutBoundsTagIndex(i);
          setLastTagIndex(i - 1);
          return;
        }
        if (totalWidth >= rowContainerWidth - 20) {
          setLastTagIndex(i);
          return;
        }
      }
    }
  }, [expandTags]);

  const inceptionDateOriginal = new Date(inceptionDate);
  const inceptionDate1Yr = new Date(inceptionDate).setFullYear(
    inceptionDateOriginal.getFullYear() + 1
  );
  const formattedAsOfDate = new Date(format.oaDate(asOfDate || 0));

  const isLinkedPerformance = {
    // TCIsLinkedPerf1Yr is set to 0 in Screener 296, add this condition to calculate 1 year link pref on front end,
    1: item[FIELDS.TCISLINKEDPERF1YR] || inceptionDate1Yr > formattedAsOfDate,
    3: item[FIELDS.TCISLINKEDPERF3YR],
    5: item[FIELDS.TCISLINKEDPERF5YR],
    10: item[FIELDS.TCISLINKEDPERF10YR]
  };
  const fontStyle = showSalesCharge ? 'font-italic' : '';
  const expandRowDrawer = () => {
    if (!isExpanded) {
      onClickExpandRow(index);
    }
  };

  const renderChart = () => {
    if (shouldShowHypoChart(fundType, isCITFund)) {
      return (
        <HypotheticalGrowthChart
          isCIT={isCITFund}
          productType={productType}
          xid={xid}
          assetClass={assetClass}
          inceptionDate={inceptionDate}
        />
      );
    } else if (isTiaaFixedAnnuity(fundType) && isProp) {
      return <CreditRatingChart />;
    }
    // else if (isModel(fundType)) {
    //   return <AssetAllocationsChart productType={productType} />;
    // }
    return <MVPEmptyState />;
  };
  return (
    <InteractiveElement
      {...className(
        'result-table-row',
        'row-container',
        index % 2 === 0 ? 'odd-row-color' : '',
        isExpanded ? '' : 'pointer'
      )}
      onClick={() => expandRowDrawer()}
    >
      <button
        {...className(
          'btn-expand',
          isExpanded ? 'expanded-color' : `${collapsedColor}`,
          isExpanded ? 'expanded' : '',
          'column-row-toggle',
          'btn-reset',
          'no-print'
        )}
        onClick={() => onClickExpandRow(index)}
        aria-expanded={isExpanded}
        aria-controls={`expand-content-row-${index + 1}`}
        aria-label={`${isExpanded ? 'Close' : 'Expand'} extra content for row ${index + 1}`}
        tabIndex={'0'}
        // TIAA Analytics Tracking
        data-analytics-linkname={`${fundName} [${fundTicker}]:InvestmentResults:${
          isExpanded ? 'Collapse' : 'Expand'
        }`}
        data-analytics-section="component"
        data-analytics-componenttype="Toggle"
        // END TIAA Analytics Tracking
      >
        <div {...className(isExpanded ? 'arrow-up' : 'arrow-down')} />
      </button>
      <div {...className('column-name')}>
        <InvestmentName url={profileUrl} fundName={fundName} item={item} />
      </div>
      <div {...className('column-asset-class')}>
        <span {...className('header-width', 'text-nowrap')}>
          {item[FIELDS.ASSETCLASS]}
          <sup>{item[FOOTNOTE_DISCLAIMERS_NUMBERS.ASSETCLASSDISNUM]}</sup>
        </span>
      </div>
      <div {...className('column-ytd')}>
        {/*TIAA-13523 Sales charge YTD data does not exist currently, so always show fund YTD*/}
        <PerformanceElement
          returnValue={yearToDateReturn}
          isLinkedPerformance={isLinkedPerformance}
          showSalesCharge={false}
        />
      </div>
      <div {...className('column-1y')}>
        <PerformanceElement
          returnValue={oneYearReturn}
          isLinkedPerformance={isLinkedPerformance}
          showSalesCharge={showSalesChargeData}
          year={'1'}
        />
      </div>
      <div {...className('column-3y')}>
        <PerformanceElement
          returnValue={threeYearReturn}
          isLinkedPerformance={isLinkedPerformance}
          showSalesCharge={showSalesChargeData}
          year={'3'}
        />
      </div>
      <div {...className('column-5y')}>
        <PerformanceElement
          returnValue={fiveYearReturn}
          isLinkedPerformance={isLinkedPerformance}
          showSalesCharge={showSalesChargeData}
          year={'5'}
        />
      </div>
      <div {...className('column-10y')}>
        <PerformanceElement
          returnValue={tenYearReturn}
          isLinkedPerformance={isLinkedPerformance}
          showSalesCharge={showSalesChargeData}
          year={'10'}
        />
      </div>
      <div {...className('column-inception')}>
        <div {...className('numbers', getTextValueColorClass(sinceIncep))}>
          {/* TIAA-11742 | Added caret for sinceInception, likely temporary*/}
          <PerformanceElement
            returnValue={sinceIncep}
            isLinkedPerformance={isLinkedPerformance}
            showSalesCharge={showSalesChargeData}
            year={'SI'}
          />
          {/* TIAA-12394 | R2 R3 and R4 since inception data point will always be hypothetical */}
          {(shareClass === 'Class R1' ||
            shareClass === 'Class R2' ||
            shareClass === 'Class R4') && <span {...className('text-dark')}>*</span>}
        </div>
        <div {...className('grey-text')}>{inceptionDate}</div>
        {isShowUnderlyingFundIncepDate && (
          <div {...className('grey-text')}>{format.oaDate(underlyingFundIncepDate)}</div>
        )}
      </div>
      <div {...className('column-expenses')}>
        {hasNetExpense ? (
          <div {...className('numbers')}>
            <PerformanceValue value={item[FIELDS.TCNETEXPENSES]} isStyleTextColor={false} />
          </div>
        ) : (
          <div {...className('numbers')}>N/A</div>
        )}
        {hasGrossExpense ? (
          <div {...className('no-print')}>
            {showNetGrossDisclosure && (
              <NetGrossDisclosureButton setModal={setModal} xid={xid} item={item} />
            )}
            <span {...className('grey-text')}>
              <PerformanceValue
                value={item[FIELDS.TCGROSSEXPENSE]}
                isStyleTextColor={false}
                sizeKey={'min'}
              />
            </span>
          </div>
        ) : (
          <div {...className('grey-text', 'grey-text-margin')}>N/A</div>
        )}
      </div>
      <div {...className('column-rating')}>
        <RatingRow
          outOfFunds={item[FIELDS.TCOVERALLRATINGOUFOFNUMBER]}
          rating={item[FIELDS.MSTARRATINGOVERALL]}
          category={item[FIELDS.TCMSTARCATEGORY]}
        />
      </div>
      <div {...className('column-invest')}>
        {investButtonMode !== INVEST_BUTTON_MODES.hide && (
          <button
            {...className('invest-btn', 'mb-2', 'screener-result-table-results-invest')}
            // TIAA Analytics Tracking
            data-analytics-linkname={`${fundName} [${fundTicker}]:InvestmentResults:Invest`}
            data-analytics-section="component"
            data-analytics-componenttype="Button"
            // END TIAA Analytics Tracking
            onClick={onClickInvestButton}
          >
            Invest
          </button>
        )}
        {item.isOwned && (
          <div {...className('d-flex flex-row')}>
            <Icon variant="InvestedCheck" />
            <div {...className('invested')}>INVESTED</div>
          </div>
        )}
        {isPlanInContext && restrictionText.length > 0 && (
          <ButtonIconLink
            text="Restriction"
            onClick={e => {
              e.stopPropagation();
              setModal('Restriction', joinedRestrictions);
            }}
            variant="restriction"
            buttonClasses={['mt-4', 'mr-1', 'justify-content-center']}
            textClass="btn-icon--fund-details"
            componentTag="Modal"
            fundName={fundName}
            fundTicker={fundTicker}
          />
        )}
      </div>
      <div {...className('column-span-performance-as-of', showFundAsOfDate ? '' : 'hidden')}>
        {showFundAsOfDate && (
          <div {...className('as-of-alternate')}>
            As of &nbsp;
            {asOfDate ? format.oaDate(asOfDate) : '--'}
          </div>
        )}
      </div>

      <div {...className('modals')}>
        <ModalInvest
          show={isInvestModalVisible}
          onHide={handleCloseInvestModal}
          fundNameOrType={fundName}
        />
        <DocumentsModal
          onHide={() => setShowModal(false)}
          show={showModal}
          modalTitle={modalTitle}
          modalContent={modalContent}
        />
      </div>

      {/*--------------------------------------------------------------
        Expanded content of the row
      --------------------------------------------------------------*/}
      {isExpanded && (
        <div
          {...className('row-expanded', 'no-print', index % 2 === 0 ? 'odd-row-color' : '')}
          id={`expand-content-row-${index + 1}`}
        >
          <div {...className('column-risk-rates')}>
            <FundDetailTable
              fundDetails={fundDetailTableData}
              fundType={fundType}
              assetClass={assetClass}
              isProp={isProp}
            />
          </div>
          <div {...className('column-chart')}>{renderChart()}</div>
          <div {...className('column-fund-buttons')}>
            <Button
              variant={'outline-button-primary'}
              {...className(
                'btn--rounded',
                'view-more-detail',
                'font-family-agenda',
                'd-flex',
                'align-items-center',
                'mb-4',
                'mt-6'
              )}
              size="sm"
              role="button"
              as="a"
              href={profileUrl}
              rel="noopener noreferrer"
              target="_blank"
              // TIAA Analytics Tracking
              data-analytics-linkname={`${fundName} [${fundTicker}]:InvestmentResults:View_fund_detail`}
              data-analytics-section="component"
              data-analytics-componenttype="Link"
              // END TIAA Analytics Tracking
            >
              View more detail
            </Button>
            <FundDetailButtons
              setModal={setModal}
              fundType={fundType}
              xid={xid}
              item={item}
              fundName={fundName}
              fundTicker={fundTicker}
            />
          </div>
          <div {...className('column-tags')}>
            {tags &&
              tags.map((tag, index2) => {
                return (
                  <span
                    data-key={`${tag.tag || ''}-${index2}`}
                    key={`${tag.tag || ''}-${index2}`}
                    ref={el => {
                      tagRef.current[index2] = el;
                    }}
                  >
                    <Tag
                      label={tag.tag}
                      popoverMsg={tag.hoverText}
                      index={index2}
                      lastTagIndex={lastTagIndex}
                      outBoundsTagIndex={outBoundsTagIndex}
                    />
                  </span>
                );
              })}
            <InteractiveElement
              as="span"
              {...className('expand-tags-btn')}
              onClick={() => setExpandTags(!expandTags)}
            >
              {expandTags ? '- Show less' : '+ Show more'}
            </InteractiveElement>
          </div>
        </div>
      )}
    </InteractiveElement>
  );
};

ResultTableRow.propTypes = {
  isExpanded: PropTypes.bool,
  item: PropTypes.object,
  index: PropTypes.number,
  isSignatureResult: PropTypes.bool,
  onClickExpandRow: PropTypes.func,
  performanceQuarterly: PropTypes.bool,
  participantHoldings: PropTypes.array,
  performanceAsOf: PropTypes.number,
  isSearchedPlanInContext: PropTypes.bool,
  searchedHoldings: PropTypes.array,
  showSalesCharge: PropTypes.bool,
  planInfo: PropTypes.array
};

ResultTableRow.defaultProps = {
  isSignatureResult: false
};

const mapStateToProps = state => {
  return {
    participantHoldings: state.filterReducer.participantHoldings,
    searchedHoldings: state.filterReducer.searchedHoldings,
    isSearchedPlanInContext: state.filterReducer.inputs.isSearchedPlanInContext,
    planInfo: state.filterReducer.planInfo
  };
};

export default connect(mapStateToProps, null)(ResultTableRow);
